import React, { useState, useEffect, useRef } from "react";
import { database } from "../firebaseConfig";
import { ref, update, onValue } from "firebase/database";
import "./WorldSettings.css";

const WhatsappSettings = ({ userId }) => {
  const [whatsappSettings, setWhatsappSettings] = useState({
    whatsappNumber: "",
    whatsappMessage: ""
  });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [availableSnippets, setAvailableSnippets] = useState([
    "lead_name", "lead_email", "lead_telephone", "lead_address", 
    "lead_city", "lead_rating", "lead_reviews", "lead_stars", 
    "business_name", "lead_instagram", "lead_website", "lead_type"
  ]);
  const [showSnippetsMenu, setShowSnippetsMenu] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const editorRef = useRef(null);
  const snippetsMenuRef = useRef(null);

  // Lade vorhandene WhatsApp-Einstellungen aus der Datenbank
  useEffect(() => {
    if (!userId) return;
    
    const whatsappSettingsRef = ref(database, `/World/${userId}/settings/whatsapp`);
    const unsubscribe = onValue(
      whatsappSettingsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setWhatsappSettings(snapshot.val());
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error loading WhatsApp settings", error);
        setLoading(false);
      }
    );
    
    return () => unsubscribe();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setWhatsappSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMessageChange = (e) => {
    const value = e.target.value;
    setWhatsappSettings((prev) => ({
      ...prev,
      whatsappMessage: value
    }));
    setCursorPosition(e.target.selectionStart);
  };

  const handleSave = async () => {
    if (!userId) {
      setMessage("No user logged in.");
      return;
    }
    
    try {
      // Formatiere die Telefonnummer (entferne Leerzeichen und andere nicht-numerische Zeichen)
      const formattedNumber = whatsappSettings.whatsappNumber.replace(/\s+/g, "").replace(/[^0-9+]/g, "");
      
      const whatsappSettingsRef = ref(database, `/World/${userId}/settings/whatsapp`);
      await update(whatsappSettingsRef, {
        ...whatsappSettings,
        whatsappNumber: formattedNumber
      });
      
      setMessage("WhatsApp settings saved successfully.");
    } catch (error) {
      console.error("Error saving WhatsApp settings:", error);
      setMessage("Error saving settings: " + error.message);
    }
  };

  const insertSnippet = (snippet) => {
    const textareaElement = editorRef.current;
    if (!textareaElement) return;
    
    const start = textareaElement.selectionStart;
    const end = textareaElement.selectionEnd;
    const text = whatsappSettings.whatsappMessage;
    
    const snippetText = `{{${snippet}}}`;
    const newText = text.substring(0, start) + snippetText + text.substring(end);
    
    setWhatsappSettings(prev => ({
      ...prev,
      whatsappMessage: newText
    }));
    
    // Schließe das Snippets-Menü
    setShowSnippetsMenu(false);
    
    // Nach der Aktualisierung des Textes den Cursor nach dem eingefügten Snippet positionieren
    setTimeout(() => {
      textareaElement.focus();
      const newPosition = start + snippetText.length;
      textareaElement.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const toggleSnippetsMenu = () => {
    setShowSnippetsMenu(!showSnippetsMenu);
  };

  // Schließe das Snippets-Menü, wenn außerhalb geklickt wird
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (snippetsMenuRef.current && !snippetsMenuRef.current.contains(event.target) && 
          !event.target.classList.contains('insertSnippetButton')) {
        setShowSnippetsMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Generiere eine Vorschau der WhatsApp-Nachricht mit hervorgehobenen Snippets
  const generatePreview = () => {
    if (!whatsappSettings.whatsappMessage) return "";
    
    // Ersetze alle Snippets im Format {{snippet}} mit einer hervorgehobenen Darstellung
    return whatsappSettings.whatsappMessage.replace(
      /\{\{([a-zA-Z_]+)\}\}/g, 
      '<span class="highlightedSnippet">{{$1}}</span>'
    );
  };

  if (loading) {
    return <p className="loadingMessage">Loading WhatsApp settings...</p>;
  }

  return (
    <div className="settingsGroup">
      <h3>WhatsApp Settings</h3>
      
      <div className="settingField">
        <label>WhatsApp Phone Number:</label>
        <input
          type="text"
          name="whatsappNumber"
          value={whatsappSettings.whatsappNumber}
          onChange={handleChange}
          placeholder="e.g. +49123456789"
          className="whatsappNumberInput"
        />
        <p className="settingHelp">
          Enter the WhatsApp number with country code (e.g. +491234567890).
        </p>
      </div>
      
      <div className="settingField">
        <label>WhatsApp Message:</label>
        <div className="whatsappMessageEditor">
          <div className="editorToolbar">
            <button 
              type="button" 
              className="insertSnippetButton"
              onClick={toggleSnippetsMenu}
            >
              + Insert Smart Snippet
            </button>
            
            {showSnippetsMenu && (
              <div ref={snippetsMenuRef} className="snippetsMenu">
                <div className="snippetsMenuHeader">
                  <h4>Available Snippets</h4>
                  <button 
                    className="closeSnippetsMenu"
                    onClick={() => setShowSnippetsMenu(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="snippetsList">
                  {availableSnippets.map((snippet) => (
                    <button
                      key={snippet}
                      className="snippetItem"
                      onClick={() => insertSnippet(snippet)}
                    >
                      {snippet}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <textarea
            ref={editorRef}
            name="whatsappMessage"
            value={whatsappSettings.whatsappMessage}
            onChange={handleMessageChange}
            placeholder="Enter your message here. Add Smart Snippets to use dynamic values."
            rows="8"
            className="whatsappMessageTextarea"
          />
        </div>
        <p className="settingHelp">
          Compose your WhatsApp message. You can insert Smart Snippets like {"{{"}"lead_name"{"}}"}
          which will be replaced with actual values later.
        </p>
      </div>
      
      <div className="whatsappPreview">
        <h4>Preview:</h4>
        <div className="whatsappPreviewContainer">
          <div className="whatsappPreviewHeader">
            <div className="whatsappPreviewContact">
              <span className="whatsappPreviewName">
                {whatsappSettings.whatsappNumber || "+49 123 456789"}
              </span>
            </div>
          </div>
          <div 
            className="whatsappPreviewMessage"
            dangerouslySetInnerHTML={{ __html: generatePreview() }}
          />
        </div>
      </div>
      
      <div className="settingActions">
        <button className="saveSettingsButton" onClick={handleSave}>
          Save Settings
        </button>
      </div>
      
      {message && <p className="settingsMessage">{message}</p>}
    </div>
  );
};

export default WhatsappSettings; 